import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MdGroup, MdDelete, MdToggleOn, MdToggleOff, MdStar } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

export default function CompanyUsersPanel({ companyId }) {
    const { t } = useLanguage();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/companies/${companyId}/users`);
            setUsers(res.data.data || []);
        } catch {
            toast.error(t('companyUsers.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [companyId, t]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const updateUser = async (userId, patch) => {
        setSavingId(userId);
        // optimistic update
        setUsers(prev => prev.map(u => u._id === userId ? { ...u, ...patch, permissions: { ...u.permissions, ...(patch.permissions || {}) } } : u));
        try {
            await api.put(`/companies/${companyId}/users/${userId}`, patch);
        } catch (err) {
            toast.error(err.response?.data?.message || t('companyUsers.updateFailed'));
            fetchUsers();
        } finally {
            setSavingId(null);
        }
    };

    const togglePermission = (user, key) => {
        updateUser(user._id, { permissions: { ...user.permissions, [key]: !user.permissions?.[key] } });
    };

    const toggleDataAlert = (user) => {
        const current = user.dataAlert || { enabled: false, language: 'tr' };
        updateUser(user._id, { dataAlert: { ...current, enabled: !current.enabled } });
    };

    const setDataAlertLanguage = (user, language) => {
        const current = user.dataAlert || { enabled: false, language: 'tr' };
        updateUser(user._id, { dataAlert: { ...current, language } });
    };

    const toggleActive = (user) => {
        updateUser(user._id, { isActive: !user.isActive });
    };

    const handleDelete = async (user) => {
        if (!window.confirm(t('companyUsers.deleteConfirm', { name: user.displayName || user.username }))) return;
        try {
            setDeletingId(user._id);
            await api.delete(`/companies/${companyId}/users/${user._id}`);
            toast.success(t('companyUsers.deleted'));
            setUsers(prev => prev.filter(u => u._id !== user._id));
        } catch (err) {
            toast.error(err.response?.data?.message || t('companyUsers.deleteFailed'));
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <motion.div className="card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 24 }}>
            <div className="card-header">
                <div className="card-title">
                    <div className="card-title-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                        <MdGroup />
                    </div>
                    {t('companyUsers.title')}
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {t('companyUsers.count', { count: users.length })}
                </span>
            </div>
            <div style={{ padding: 0 }}>
                {loading ? (
                    <div className="loading-container"><div className="spinner"></div></div>
                ) : users.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-desc">{t('companyUsers.empty')}</div>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>{t('companyUsers.user')}</th>
                                    <th>{t('companyUsers.email')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('companyUsers.permOutputs')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('companyUsers.permThreshold')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('companyUsers.permDownload')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('companyUsers.dataAlert')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('companyUsers.dataAlertLang')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('companyUsers.active')}</th>
                                    <th style={{ width: 60 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u._id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ fontWeight: 600 }}>{u.displayName || u.username}</span>
                                                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>({u.username})</span>
                                                {u.isOwner && (
                                                    <span className="badge badge-primary" style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <MdStar size={11} /> {t('companyUsers.owner')}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{u.email || '—'}</td>
                                        {['canTriggerOutputs', 'canEditThreshold', 'canDownloadData'].map(key => (
                                            <td key={key} style={{ textAlign: 'center' }}>
                                                {u.isOwner ? (
                                                    <MdToggleOn size={22} style={{ color: 'var(--success)', opacity: 0.5 }} title={t('companyUsers.ownerAlwaysAllowed')} />
                                                ) : (
                                                    <button
                                                        onClick={() => togglePermission(u, key)}
                                                        disabled={savingId === u._id}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', margin: '0 auto' }}
                                                        title={t(`companyUsers.${key}`)}
                                                    >
                                                        {u.permissions?.[key]
                                                            ? <MdToggleOn size={26} style={{ color: 'var(--success)' }} />
                                                            : <MdToggleOff size={26} style={{ color: 'var(--text-muted)' }} />}
                                                    </button>
                                                )}
                                            </td>
                                        ))}
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                onClick={() => toggleDataAlert(u)}
                                                disabled={savingId === u._id}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', margin: '0 auto' }}
                                                title={t('companyUsers.dataAlertHint')}
                                            >
                                                {u.dataAlert?.enabled
                                                    ? <MdToggleOn size={26} style={{ color: 'var(--success)' }} />
                                                    : <MdToggleOff size={26} style={{ color: 'var(--text-muted)' }} />}
                                            </button>
                                            {u.dataAlert?.enabled && !u.email && !u.isOwner && (
                                                <div style={{ fontSize: 10, color: 'var(--danger)', marginTop: 2 }}>{t('companyUsers.dataAlertNoEmail')}</div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <select
                                                value={u.dataAlert?.language || 'tr'}
                                                onChange={(e) => setDataAlertLanguage(u, e.target.value)}
                                                disabled={savingId === u._id || !u.dataAlert?.enabled}
                                                style={{
                                                    background: 'var(--bg-body)', border: '1px solid var(--border)',
                                                    borderRadius: 6, color: 'var(--text-primary)', fontSize: 12,
                                                    padding: '4px 6px', cursor: 'pointer'
                                                }}
                                            >
                                                <option value="tr">TR</option>
                                                <option value="en">EN</option>
                                            </select>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {u.isOwner ? (
                                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                                            ) : (
                                                <button
                                                    onClick={() => toggleActive(u)}
                                                    disabled={savingId === u._id}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', margin: '0 auto' }}
                                                >
                                                    {u.isActive
                                                        ? <MdToggleOn size={26} style={{ color: 'var(--success)' }} />
                                                        : <MdToggleOff size={26} style={{ color: 'var(--danger)' }} />}
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            {!u.isOwner && (
                                                <button
                                                    className="btn btn-danger btn-sm btn-icon"
                                                    onClick={() => handleDelete(u)}
                                                    disabled={deletingId === u._id}
                                                    style={{ width: 28, height: 28 }}
                                                >
                                                    {deletingId === u._id ? <span className="spinner spinner-sm" /> : <MdDelete size={14} />}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
